import { SET_OPS, isSetOp, type ClauseOp } from '@kkp-fraud/shared';
import { ACTIONS, AGGREGATES, EVENTS, FIELD_KINDS, FIELD_NAMES, SCOPES, customFieldNames, kindOf, opsFor, type FieldKind, type FieldName } from '../fields';
import { t } from '../i18n';
import { WINDOW_CHOICES, windowChoices, type WindowUnit } from '../longWindows';
import { COND_PREFIX, type RuleKindShort } from './state';

/**
 * Our block definitions, typed here rather than copied from a CDN snippet.
 *
 * Three rules this file exists to keep:
 *   1. The palette can only say what `packages/shared` can persist — a flat clause list.
 *      There is no nesting block; conditions stack, one block per clause. See `state.ts`.
 *   2. The choices are exactly the clause editor's choices: the closed field list from
 *      `fields.ts`, operators from `opsFor(kind)`, a value editor per field kind. A field
 *      no selected source sends is not offered at all (PLAN-v2 §1.3), never offered-and-
 *      disabled — which is why the options are built per rule, not once at module load.
 *   3. Every label of ours goes through `t()` under `block.<subject>.<role>`, and every
 *      message uses %1/%2 so a translator can move the slots. Field names, scopes, events
 *      and aggregates stay verbatim: they are the engine's identifiers, shown the same way
 *      in the clause editor, not prose.
 */

/** Blockly's JSON block definition — only the parts we actually use. */
export interface BlockDefinition {
  type: string;
  message0?: string;
  args0?: unknown[];
  message1?: string;
  args1?: unknown[];
  message2?: string;
  args2?: unknown[];
  message3?: string;
  args3?: unknown[];
  message4?: string;
  args4?: unknown[];
  message5?: string;
  args5?: unknown[];
  output?: string | null;
  previousStatement?: string | null;
  nextStatement?: string | null;
  inputsInline?: boolean;
  style?: string;
  extensions?: string[];
  tooltip?: string;
}

type Option = [string, string];

/**
 * The little this file needs from the Blockly field a dropdown generator is bound to.
 * Blockly's `FieldDropdown` accepts either an option array or a generator function, and
 * `FieldDropdown.fromJson` hands whatever is in `options` straight to the constructor — so a
 * function here reaches Blockly intact, and nothing in this module has to import Blockly.
 */
interface DropdownFieldThis {
  getSourceBlock(): { getFieldValue(name: string): string | null } | null;
  getValue?(): string | null;
}

/** A dropdown whose options depend on the state of the block it sits on. */
export type OptionGenerator = (this: DropdownFieldThis) => Option[];

const opOption = (op: ClauseOp): Option => [t(`block.${op}.label`), op];
const actionOptions = (): Option[] => ACTIONS.map((a) => [t(`block.${a}.label`), a]);
const logicOptions = (): Option[] => [
  [t('block.AND.label'), 'AND'],
  [t('block.OR.label'), 'OR'],
];
const unitOptions = (): Option[] => [
  [t('block.seconds.label'), '1'],
  [t('block.minutes.label'), '60'],
  [t('block.hours.label'), '3600'],
  // Wave 7 long windows — the same day-based units as the Sentence view (a month = 30 days, a year = 365)
  [t('unit.day.label'), '86400'],
  [t('unit.month.label'), '2592000'],
  [t('unit.year.label'), '31536000'],
];

/**
 * The fields a rule may choose from: what its sources send, plus what it already uses — the
 * standard fields, then the set's custom fields (Wave 6, `useCustomFields`).
 */
export function offeredFields(allowed: readonly FieldName[], used: readonly FieldName[]): FieldName[] {
  const set = new Set<FieldName>([...allowed, ...used.filter((f) => kindOf(f) !== undefined)]);
  return [...FIELD_NAMES, ...customFieldNames()].filter((f) => set.has(f));
}

function fieldOptions(fields: readonly FieldName[], pred: (kind: FieldKind) => boolean): Option[] {
  return fields
    .filter((f) => {
      const kind = kindOf(f);
      return kind !== undefined && pred(kind);
    })
    .map((f) => [f, f] as Option);
}

/** The condition families a given field set can populate — an empty dropdown is not legal. */
export interface Palette {
  num: Option[];
  str: Option[];
  bool: Option[];
  listable: Option[];
}

export function paletteFor(fields: readonly FieldName[]): Palette {
  return {
    num: fieldOptions(fields, (k) => k === 'number'),
    str: fieldOptions(fields, (k) => k === 'string'),
    bool: fieldOptions(fields, (k) => k === 'boolean'),
    listable: fieldOptions(fields, (k) => k !== 'boolean'),
  };
}

/** A scalar comparison: everything but the list, range and Master list ops, which have their own blocks. */
const scalar = (o: ClauseOp) => o !== 'in' && o !== 'nin' && o !== 'between' && !isSetOp(o);
const NUM_OPS: ClauseOp[] = opsFor('number').filter(scalar);
const STR_OPS: ClauseOp[] = opsFor('string').filter(scalar);
const BOOL_OPS: ClauseOp[] = opsFor('boolean');
const LIST_OPS: ClauseOp[] = ['in', 'nin'];
const SET_BLOCK_OPS: ClauseOp[] = [...SET_OPS];

/**
 * The six condition blocks of one family. `rb_cond_*` carry the AND/OR join; `pb_filter_*`
 * do not, because the engine ANDs pattern filters and the clause editor hides the column
 * there too (`RuleEditor.tsx` passes `showLogic={false}`). Offering OR on a filter would be
 * a promise the document cannot keep.
 */
export function conditionBlocks(kind: RuleKindShort, palette: Palette): BlockDefinition[] {
  const prefix = COND_PREFIX[kind];
  const check = `${prefix}`;
  const withLogic = kind === 'rb';
  const logicArg = () => (withLogic ? [{ type: 'field_dropdown', name: 'LOGIC', options: logicOptions() }] : []);
  const key = (subject: string) => `block.${subject}.${withLogic ? 'joined' : 'plain'}`;
  const common = { previousStatement: check, nextStatement: check, inputsInline: true, style: 'cond_blocks' };
  const out: BlockDefinition[] = [];

  if (palette.num.length > 0) {
    out.push({
      type: `${prefix}_num`,
      message0: t(key('cond_num')),
      args0: [
        ...logicArg(),
        { type: 'field_dropdown', name: 'FIELD', options: palette.num },
        { type: 'field_dropdown', name: 'OP', options: NUM_OPS.map(opOption) },
        { type: 'field_number', name: 'VAL', value: 0 },
      ],
      tooltip: t('block.cond_num.tooltip'),
      ...common,
    });
    out.push({
      type: `${prefix}_range`,
      message0: t(key('cond_range')),
      args0: [
        ...logicArg(),
        { type: 'field_dropdown', name: 'FIELD', options: palette.num },
        { type: 'field_number', name: 'MIN', value: 0 },
        { type: 'field_number', name: 'MAX', value: 0 },
      ],
      tooltip: t('block.cond_range.tooltip'),
      ...common,
    });
  }

  if (palette.str.length > 0) {
    out.push({
      type: `${prefix}_str`,
      message0: t(key('cond_str')),
      args0: [
        ...logicArg(),
        { type: 'field_dropdown', name: 'FIELD', options: palette.str },
        { type: 'field_dropdown', name: 'OP', options: STR_OPS.map(opOption) },
        { type: 'field_input', name: 'VAL', text: '' },
      ],
      tooltip: t('block.cond_str.tooltip'),
      ...common,
    });
  }

  if (palette.listable.length > 0) {
    out.push({
      type: `${prefix}_list`,
      message0: t(key('cond_list')),
      args0: [
        ...logicArg(),
        { type: 'field_dropdown', name: 'FIELD', options: palette.listable },
        { type: 'field_dropdown', name: 'OP', options: LIST_OPS.map(opOption) },
        { type: 'field_input', name: 'VAL', text: '' },
      ],
      tooltip: t('block.cond_list.tooltip'),
      ...common,
    });
  }

  if (palette.listable.length > 0) {
    // R7: the field is in a Master list — the list named by its id, typed (see `state.ts`).
    out.push({
      type: `${prefix}_set`,
      message0: t(key('cond_set')),
      args0: [
        ...logicArg(),
        { type: 'field_dropdown', name: 'FIELD', options: palette.listable },
        { type: 'field_dropdown', name: 'OP', options: SET_BLOCK_OPS.map(opOption) },
        { type: 'field_input', name: 'VAL', text: '' },
      ],
      tooltip: t('block.cond_set.tooltip'),
      ...common,
    });
  }

  if (palette.bool.length > 0) {
    out.push({
      type: `${prefix}_bool`,
      message0: t(key('cond_bool')),
      args0: [
        ...logicArg(),
        { type: 'field_dropdown', name: 'FIELD', options: palette.bool },
        { type: 'field_dropdown', name: 'OP', options: BOOL_OPS.map(opOption) },
        {
          type: 'field_dropdown',
          name: 'VAL',
          options: [
            [t('block.true.label'), 'true'],
            [t('block.false.label'), 'false'],
          ],
        },
      ],
      tooltip: t('block.cond_bool.tooltip'),
      ...common,
      style: 'bool_blocks',
    });
  }

  return out;
}

/**
 * The AGGFIELD dropdown, which is not a fixed list: it depends on the AGG field beside it.
 *
 * `sum` adds the values up, so only a numeric field can be summed — `validate.ts:100` calls a
 * non-numeric one a problem and the form only offers numeric fields for it
 * (`components/RuleEditor.tsx:426`). The block view offered every field regardless, so the
 * two editors over the same rule disagreed about what was even choosable. They do not now.
 *
 * `distinct` counts distinct values of anything, and `count` takes no field at all — hence
 * the explicit "nothing" entry, because a dropdown with no option is not a legal Blockly field.
 *
 * The current value is always kept in the list. Blockly rejects a field value that is not
 * among the options, and deserialization may set AGGFIELD before AGG — so narrowing without
 * this guard would silently mangle a stored rule. An invalid stored value stays visible, and
 * stays flagged by the problem list, which is how it gets noticed and fixed.
 */
export function aggregateFieldOptions(fields: readonly FieldName[]): OptionGenerator {
  const none: Option = [t('block.aggfield.none'), ''];
  const all: Option[] = [none, ...fields.map((f) => [f, f] as Option)];
  const numeric: Option[] = [none, ...fields.filter((f) => FIELD_KINDS[f] === 'number').map((f) => [f, f] as Option)];

  return function aggregateFieldMenu(this: DropdownFieldThis): Option[] {
    const block = typeof this?.getSourceBlock === 'function' ? this.getSourceBlock() : null;
    if (block?.getFieldValue('AGG') !== 'sum') return all;

    const current = block.getFieldValue('AGGFIELD');
    if (current && !numeric.some(([, value]) => value === current)) return [...numeric, [current, current]];
    return numeric;
  };
}

/**
 * The window's number, a dropdown whose list depends on the UNIT dropdown before it — the same
 * lists as the Sentence view (`WINDOW_CHOICES`: days 1–29, months 1–12, one year …), plus the
 * field's own value when the list lacks it, so an existing 200-day rule still reads "200 days".
 *
 * Blockly checks a new dropdown value against the options it generated LAST (at construction,
 * before the block exists, until the menu is next opened), so a plain `field_dropdown` would
 * refuse a stored 200 while loading and silently keep its default. The field is therefore
 * `field_window_number` — a `FieldDropdown` registered by `BlocksWorkspace` that accepts any
 * whole number and shows its own value (`windowNumberField`); this function is its menu.
 */
export function windowNumberOptions(): OptionGenerator {
  const toOptions = (list: readonly number[]): Option[] => list.map((n) => [String(n), String(n)] as Option);
  return function windowNumberMenu(this: DropdownFieldThis): Option[] {
    const block = typeof this?.getSourceBlock === 'function' ? this.getSourceBlock() : null;
    const unit = Number(block?.getFieldValue('UNIT'));
    if (!block || !(unit in WINDOW_CHOICES)) return toOptions(WINDOW_CHOICES[1]);
    const own = Number(typeof this.getValue === 'function' ? this.getValue() : Number.NaN);
    return toOptions(windowChoices(unit as WindowUnit, own * unit));
  };
}

/** The block field type of the window's number (see `windowNumberOptions`). */
export const WINDOW_NUMBER_FIELD = 'field_window_number';

export function hatBlock(kind: RuleKindShort, fields: readonly FieldName[]): BlockDefinition {
  if (kind === 'rb') {
    return {
      type: 'rb_when',
      message0: t('block.rb_when.condition'),
      args0: [{ type: 'input_statement', name: 'COND', check: COND_PREFIX.rb }],
      message1: t('block.rb_when.action'),
      args1: [{ type: 'field_dropdown', name: 'ACTION', options: actionOptions() }],
      style: 'hat_blocks',
      extensions: ['kkp_hat'],
      tooltip: t('block.rb_when.tooltip'),
    };
  }

  const aggFieldOptions = aggregateFieldOptions(fields);

  return {
    type: 'pb_when',
    message0: t('block.pb_when.scope'),
    args0: [{ type: 'field_dropdown', name: 'SCOPE', options: SCOPES.map((s) => [s, s] as Option) }],
    message1: t('block.pb_when.aggregate'),
    args1: [
      { type: 'field_dropdown', name: 'AGG', options: AGGREGATES.map((a) => [a, a] as Option) },
      { type: 'field_dropdown', name: 'EVENT', options: EVENTS.map((e) => [e, e] as Option) },
      { type: 'field_number', name: 'THR', value: 3, min: 0 },
    ],
    message2: t('block.pb_when.aggregateField'),
    args2: [{ type: 'field_dropdown', name: 'AGGFIELD', options: aggFieldOptions }],
    // The unit first, then the number from that unit's list — as the Sentence view (owner, 30 Sep).
    message3: t('block.pb_when.window'),
    args3: [
      { type: 'field_dropdown', name: 'UNIT', options: unitOptions() },
      { type: WINDOW_NUMBER_FIELD, name: 'WIN', options: windowNumberOptions() },
    ],
    message4: t('block.pb_when.filters'),
    args4: [{ type: 'input_statement', name: 'COND', check: COND_PREFIX.pb }],
    message5: t('block.pb_when.action'),
    args5: [{ type: 'field_dropdown', name: 'ACTION', options: actionOptions() }],
    style: 'hat_blocks',
    extensions: ['kkp_hat', 'kkp_window'],
    tooltip: t('block.pb_when.tooltip'),
  };
}

export function blockDefinitions(kind: RuleKindShort, allowed: readonly FieldName[], used: readonly FieldName[]): BlockDefinition[] {
  const fields = offeredFields(allowed, used);
  const palette = paletteFor(fields);
  return [hatBlock(kind, fields), ...conditionBlocks(kind, palette)];
}

// =============================================================================
// Theme and toolbox
// =============================================================================

/** KKP purples by role, lifted from `docs/console-mockup/blocks.js:125-147`. */
export const KKP_BLOCK_STYLES = {
  hat_blocks: { colourPrimary: '#594f74', colourSecondary: '#4a4162', colourTertiary: '#3f3755' },
  cond_blocks: { colourPrimary: '#635f98', colourSecondary: '#564f8a', colourTertiary: '#4a4477' },
  bool_blocks: { colourPrimary: '#7a5b9a', colourSecondary: '#6a4d89', colourTertiary: '#5c4277' },
};

export const KKP_COMPONENT_STYLES = {
  workspaceBackgroundColour: '#ffffff',
  toolboxBackgroundColour: '#f6f6f9',
  toolboxForegroundColour: '#3d3c3e',
  flyoutBackgroundColour: '#edecf2',
  flyoutOpacity: 1,
  scrollbarColour: '#d9d8e2',
  insertionMarkerColour: '#594f74',
};

export const KKP_FONT_STYLE = { family: 'Kanit, "Noto Sans Thai", sans-serif', weight: '400', size: 11.5 };

export interface ToolboxItem {
  kind: string;
  type?: string;
  text?: string;
  name?: string;
  colour?: string;
  contents?: ToolboxItem[];
  fields?: Record<string, string | number>;
}

/** Only the block types that were actually defined for this rule appear in the toolbox. */
export function toolboxFor(kind: RuleKindShort, definitions: readonly BlockDefinition[]): ToolboxItem {
  const defined = new Set(definitions.map((d) => d.type));
  const prefix = COND_PREFIX[kind];
  const has = (suffix: string) => defined.has(`${prefix}_${suffix}`);
  const conditions: ToolboxItem[] = [];

  if (has('num')) {
    conditions.push({ kind: 'label', text: t('block.toolbox.number') });
    conditions.push({ kind: 'block', type: `${prefix}_num` });
    conditions.push({ kind: 'block', type: `${prefix}_range` });
  }
  if (has('list')) {
    conditions.push({ kind: 'label', text: t('block.toolbox.list') });
    conditions.push({ kind: 'block', type: `${prefix}_list` });
  }
  if (has('set')) {
    conditions.push({ kind: 'label', text: t('block.toolbox.set') });
    conditions.push({ kind: 'block', type: `${prefix}_set` });
  }
  if (has('str')) {
    conditions.push({ kind: 'label', text: t('block.toolbox.text') });
    conditions.push({ kind: 'block', type: `${prefix}_str` });
  }
  if (has('bool')) {
    conditions.push({ kind: 'label', text: t('block.toolbox.boolean') });
    conditions.push({ kind: 'block', type: `${prefix}_bool` });
  }

  return {
    kind: 'categoryToolbox',
    contents: [
      {
        kind: 'category',
        name: t('block.toolbox.head'),
        colour: KKP_BLOCK_STYLES.hat_blocks.colourPrimary,
        contents: [{ kind: 'block', type: kind === 'pb' ? 'pb_when' : 'rb_when' }],
      },
      {
        kind: 'category',
        name: t('block.toolbox.conditions'),
        colour: KKP_BLOCK_STYLES.cond_blocks.colourPrimary,
        contents: conditions,
      },
    ],
  };
}
